import { useEffect, useMemo, useState } from "react";
import { Printer, Check } from "lucide-react";
import { api } from "../../api";
import { getPpeIcon } from "./pictograms";

const TASKS = [
  { id: "loading", label: "Loading / unloading tanker" },
  { id: "sampling", label: "Sampling / QC draw" },
  { id: "dosing", label: "Manual dosing / transfer" },
  { id: "maintenance", label: "Line / pump maintenance" },
];

const DURATIONS = [
  { id: "short", label: "< 30 min" },
  { id: "med", label: "30 min – 2 h" },
  { id: "long", label: "> 2 h" },
];

const CONCENTRATIONS = [
  { id: "dil", label: "Dilute (< 10 %)" },
  { id: "std", label: "Standard (10 – 50 %)" },
  { id: "conc", label: "Concentrated (> 50 %)" },
];

function extraItems(duration, conc) {
  const extras = [];
  if (duration === "long") extras.push("Hydration pack + supervised rest break every 45 min");
  if (conc === "conc") extras.push("Emergency shower & eyewash within 10 m — confirm before entry");
  if (conc === "conc" && duration !== "short") extras.push("Standby buddy with radio + eye-in-sight");
  return extras;
}

export default function KitBuilder({ chemKey, chemName }) {
  const [task, setTask] = useState("loading");
  const [duration, setDuration] = useState("med");
  const [conc, setConc] = useState("std");
  const [base, setBase] = useState([]);

  useEffect(() => {
    api.getPpe(chemKey, task).then((d) => setBase(d.items || []));
  }, [chemKey, task]);

  const items = useMemo(() => [...base, ...extraItems(duration, conc)], [base, duration, conc]);

  const [checks, setChecks] = useState({});
  useEffect(() => setChecks({}), [chemKey, task, duration, conc]);

  function printCard() {
    window.print();
  }

  const now = new Date();
  const stamp = now.toLocaleString();

  return (
    <div className="kit-builder" data-testid="kit-builder">
      <div className="kit-inputs">
        <div className="kit-input">
          <label className="mono">Task</label>
          <select value={task} onChange={(e) => setTask(e.target.value)} data-testid="kit-task">
            {TASKS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="kit-input">
          <label className="mono">Duration</label>
          <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="kit-duration">
            {DURATIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div className="kit-input">
          <label className="mono">Concentration</label>
          <select value={conc} onChange={(e) => setConc(e.target.value)} data-testid="kit-concentration">
            {CONCENTRATIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <button className="btn btn-brand btn-sm" onClick={printCard} data-testid="kit-print">
          <Printer size={14} /> Print pre-entry card
        </button>
      </div>

      <div className="kit-card" data-testid="kit-card">
        <div className="kit-card-head">
          <div>
            <div className="eyebrow">Pre-entry PPE checklist</div>
            <h3>{chemName} · {TASKS.find((t) => t.id === task)?.label}</h3>
          </div>
          <div className="kit-meta mono">
            <div>Duration: {DURATIONS.find((d) => d.id === duration)?.label}</div>
            <div>Concentration: {CONCENTRATIONS.find((d) => d.id === conc)?.label}</div>
            <div>Generated: {stamp}</div>
          </div>
        </div>

        <ul className="kit-list" data-testid="kit-list">
          {items.map((it, i) => {
            const ItemIcon = getPpeIcon(it);
            return (
              <li key={i}>
                <button
                  type="button"
                  className={"kit-check " + (checks[i] ? "on" : "")}
                  onClick={() => setChecks((c) => ({ ...c, [i]: !c[i] }))}
                  data-testid={`kit-check-${i}`}
                  aria-label={`Toggle ${it}`}
                >
                  {checks[i] ? <Check size={14} /> : <ItemIcon size={14} />}
                </button>
                <span className={checks[i] ? "kit-item done" : "kit-item"}>{it}</span>
              </li>
            );
          })}
        </ul>

        <div className="kit-signature">
          <div>
            <div className="mono kit-sig-label">Operator sign</div>
            <div className="kit-sig-line" />
          </div>
          <div>
            <div className="mono kit-sig-label">Supervisor sign</div>
            <div className="kit-sig-line" />
          </div>
        </div>
      </div>
    </div>
  );
}
